import React, { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { TextareaAutosize } from "@mui/material";
import { Alert, AlertDescription, Button, IconButton, Switch, Tooltip } from "@bolt/components";
import { ChatMessage, ChatMessageUser, ChatTheme, ERRORS, ErrorType } from "../../../types";
import "katex/dist/katex.min.css";
import ChatMessageComponent from "../../ChatMessageComponent";
import { MessageBot } from "../../MessageBot";
import SkeletonTextBlock from "../../SkeletonTextBlock";
import { LogOutButton } from "../../LogOutButton";
import { DisclosureWarning } from "../../DisclosureWarning";
import MobileSidebarToggleButton from "../../MobileSidebarToggleButton";
import ChatFooterDisclosure from "../../ChatFooterDisclosure";
import clsx from "clsx";
import { useUserSessionContext } from "../../../contexts/UserSessionContext";
import BotWelcomeMessage from "../../BotWelcomeMessage";
import { ChatToolbar } from "../../ChatToolbar";
import { CharacterLimitWarning } from "../../CharacterLimitWarning";
import { MAX_CHAR_LIMIT } from "../../../constants";
import { chatMessageErrorHandler, generateChatId } from "../../../utils";
import { useTheme } from "../../../contexts/ThemeContext";
import { usePostAskGpcQuestion, useUserQuestion } from "../../../hooks";
import { buildErrorMessage } from "../../../utils";
import { AppSelector } from "../../AppSelector";
import { AppType } from "../../../types";
import { AskGPCLogomark } from "../../Logos/Logos";

import "./ChatUiStyles.css";

export const AskGpcUi = ({
  activeApp,
  toggleSidebar,
  setActiveApp,
}: {
  activeApp: AppType;
  toggleSidebar: () => void;
  setActiveApp: Dispatch<AppType>;
}) => {
  const { setUserSession } = useUserSessionContext();
  const { theme, toggleTheme } = useTheme();
  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatState, setChatState] = useState<"ready" | "responding" | "rephrasing">("ready");
  const [chatID, setChatID] = useState<string>(generateChatId());
  const chatsContainerRef = useRef<HTMLDivElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { userQuestion, setUserQuestion, clearQuestionContext, file, setFile } = useUserQuestion();
  const { fetch: askQuestion, abort: abortQuestion } = usePostAskGpcQuestion();

  useEffect(() => {
    if (chatsContainerRef.current) chatsContainerRef.current.scrollTop = chatsContainerRef.current.scrollHeight;
  }, [chatHistory]);

  useEffect(() => {
    if (chatState === "ready") focusTextInput();
  }, [chatState]);

  useEffect(() => {
    setIsCharacterLimitExceeded(userQuestion.length > MAX_CHAR_LIMIT);
  }, [userQuestion]);

  const focusTextInput = () => {
    if (!textAreaRef.current) return;
    const textArea = textAreaRef.current;
    textArea.focus();
    textArea.setSelectionRange(textArea.value.length, textArea.value.length);
  };

  const handleAbort = useCallback(() => {
    abortQuestion();
    clearQuestionContext();
    setChatHistory((state) => [...state, buildErrorMessage({ error: ERRORS.ABORTED, errorType: ErrorType.OTHER })]);
    setChatState("ready");
  }, [abortQuestion, clearQuestionContext, setChatHistory, setChatState]);

  const handleNewChat = useCallback(() => {
    abortQuestion();
    setChatHistory([]);
    clearQuestionContext();
    setChatID(generateChatId());
  }, [abortQuestion, setChatHistory, clearQuestionContext]);

  const handleSend = async () => {
    clearQuestionContext();
    await sendMessage(userQuestion, file);
    setFile(undefined);
  };

  const sendMessage = useCallback(
    async (message: string, fileUpload?: File) => {
      setChatState("responding");
      setChatHistory((state) => [
        ...state,
        {
          content: message,
          role: "user",
          error: false,
        },
      ]);

      try {
        const result = await askQuestion({
          body: JSON.stringify({
            chat_id: chatID,
            question: message,
          }),
        });
        if (result?.data) {
          setChatHistory((state) => [...state, result.data as ChatMessage]);
        }
      } catch (error: any) {
        setChatHistory((state) => [...state, buildErrorMessage(chatMessageErrorHandler(error))]);
      } finally {
        setChatState("ready");
      }
    },
    [askQuestion, chatID],
  );

  const handleRegenerateResponse = useCallback(() => {
    const userQuestion = chatHistory
      .slice()
      .reverse()
      .find((message: ChatMessage): message is ChatMessageUser => message.role === "user");

    if (userQuestion) {
      sendMessage(userQuestion.content, userQuestion.file);
    } else {
      console.error("The user question from chatHistory is empty!");
    }
  }, [sendMessage, chatHistory]);

  const onKeyDownTextarea = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isCharacterLimitExceeded || userQuestion.length === 0) {
        e.preventDefault();
        return;
      }
      handleSend();
      e.preventDefault();
    }
  };

  const handleRedirectToRoot = useCallback(() => {
    setUserSession(null);
  }, [setUserSession]);

  return (
    <>
      <div className="sidebar">
        <AppSelector selectedApp={activeApp} setApp={setActiveApp} />
        <Button
          className="new-chat-button"
          size="lg"
          intent="primary"
          data-testid="clear-history-btn"
          onClick={handleNewChat}
          disabled={!chatHistory.length}
        >
          New chat
        </Button>
        <Alert className="history-alert" type="error" showClose={false}>
          <AlertDescription>Conversation history is not supported yet.</AlertDescription>
        </Alert>
        <div className="theme-switch-container">
          <Switch
            data-testid="dark-mode-toggle"
            onClick={toggleTheme}
            label="dark mode"
            size="md"
            checked={theme === ChatTheme.dark}
          />
        </div>
      </div>
      <div className="main">
        <div className="chat-header">
          <MobileSidebarToggleButton onClick={toggleSidebar} />
          <DisclosureWarning />
          <LogOutButton />
        </div>
        <div className="chats" data-testid="chats" ref={chatsContainerRef}>
          <BotWelcomeMessage icon={<AskGPCLogomark />} />

          {chatHistory.map((message: ChatMessage, index: number) => (
            <ChatMessageComponent
              key={index}
              message={message}
              handleRegenerateResponse={handleRegenerateResponse}
              handleRedirectToRoot={handleRedirectToRoot}
              isLatest={index === chatHistory.length - 1}
              botIcon={<AskGPCLogomark />}
            />
          ))}

          {chatState === "responding" && (
            <MessageBot icon={<AskGPCLogomark />}>
              <div style={{ width: "100%" }}>
                <SkeletonTextBlock />
              </div>
            </MessageBot>
          )}
        </div>
        <div
          className={`input-box-container ${isCharacterLimitExceeded ? "input-box-container-error" : ""}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) focusTextInput();
          }}
        >
          <div
            className={clsx("chat-text-area", {
              "error-border": isCharacterLimitExceeded,
            })}
          >
            <TextareaAutosize
              ref={textAreaRef}
              data-testid="chat-input"
              aria-label="Ask chatbot"
              draggable={false}
              disabled={chatState !== "ready"}
              placeholder="Ask me a question"
              onKeyDown={onKeyDownTextarea}
              onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setUserQuestion(e.currentTarget.value)}
              value={userQuestion}
            />
          </div>
          <ChatToolbar
            right={
              <>
                {isCharacterLimitExceeded && <CharacterLimitWarning text={userQuestion} />}
                {chatState === "rephrasing" && <div className="rephrase-status">Rephrasing...</div>}
                {chatState !== "ready" && (
                  <Tooltip content="Stop response" color="inverted">
                    <IconButton
                      ariaLabel="Stop response"
                      onClick={handleAbort}
                      className="abort-button"
                      data-testid="abort-button"
                      size="md"
                      iconName="xmark-large"
                      variant="outline"
                      // Note: This is commented because it doesn't work as expected at current version of BOLT package
                      // The override has been added at css file
                      // intent="destructive"
                    />
                  </Tooltip>
                )}
                {chatState === "ready" && (
                  <Tooltip content="Submit my prompt" color="inverted">
                    <IconButton
                      ariaLabel="Submit my prompt"
                      disabled={userQuestion === "" || isCharacterLimitExceeded}
                      className="send-button"
                      data-testid="send-button"
                      onClick={handleSend}
                      size="md"
                      iconName="chevron-right"
                      variant="outline"
                    />
                  </Tooltip>
                )}
              </>
            }
          />
        </div>
        <ChatFooterDisclosure />
      </div>
    </>
  );
};
